export type CreativeStyle = 'friendly' | 'expert' | 'sales' | 'minimal' | 'humor'

export const STYLE_LABELS: Record<CreativeStyle, string> = {
  friendly: 'Дружелюбный',
  expert: 'Экспертный',
  sales: 'Продающий',
  minimal: 'Минимал',
  humor: 'Юмор',
}

export interface ChannelAnalysis {
  about_channel: string
  target_audience: string
  pain_points: string[]
  motivations: string[]
  headlines: string[]
}

export interface Creative {
  id: string
  headline: string
  text: string
  image_url: string | null
  segment?: string
}

export interface GenerationResult {
  analysis: ChannelAnalysis
  creatives: Creative[]
  analyzed_posts_count: number
  source?: 'posts' | 'channel' | 'text'
  platform?: 'tg' | 'max'
  external_channel_id?: string
  insights?: Record<string, unknown>
  metadata?: Record<string, unknown>
}

export type GenerationStatus =
  | 'pending'
  | 'analyzing_style'
  | 'analyzing_audience'
  | 'analyzing_problems'
  | 'searching_references'
  | 'generating_headlines'
  | 'generating_texts'
  | 'generating_images'
  | 'validating'
  | 'completed'
  | 'failed'

export type GenerationErrorCode =
  | 'INSUFFICIENT_POSTS'
  | 'INSUFFICIENT_TEXT_LENGTH'
  | 'INVALID_PAYLOAD'
  | 'ANALYSIS_FAILED'
  | 'CREATIVE_AI_ERROR'

export interface GenerationErrorDetails {
  code: GenerationErrorCode
  message: string
  posts_found?: number
  posts_required?: number
  posts_without_text?: number
  retry_after_seconds?: number
  suggestion?: string
}

export interface GenerationStatusEvent {
  job_id: string
  status: GenerationStatus
  progress: number
  stage?: string
  result?: GenerationResult
  error?: string
  error_details?: GenerationErrorDetails
}

export interface Campaign {
  id: string
  name: string
  segment: string
  cpc: number
  adsCount: number
  network: 'РСЯ' | 'Поиск' | 'РСЯ + Поиск'
  status: 'черновик' | 'активна'
}

const USE_MOCKS = false

const MOCK_ANALYSIS: ChannelAnalysis = {
  about_channel:
    '«Тестовый канал TGX» — про ремесленный кофе: обзоры зёрен, рецепты, разборы кофеен Москвы и Питера. Аудитория: домашние бариста, любители спешелти, владельцы кофеен. Тон — дружелюбный, без продаж в лоб.',
  target_audience: 'кофеманы 25–40, бариста, владельцы кофеен',
  pain_points: ['не знаю с чего начать', 'дорого ходить в кофейни', 'хочу кофе не хуже специалиста'],
  motivations: ['научиться делать как профи', 'сэкономить на кофе', 'понимать вкусы'],
  headlines: [
    'Кофе как у бариста — дома',
    'С чего начать пить хороший кофе',
    'Канал, который читают бариста',
  ],
}

const MOCK_CREATIVES_TEMPLATE: Omit<Creative, 'id'>[] = [
  {
    headline: 'Кофе как у бариста — дома',
    text: 'Разбираем зёрна, прокачиваем эспрессо, делимся рецептами. 247 разборов и живое сообщество кофеманов.',
    image_url: null,
    segment: 'Сегмент · домашние бариста',
  },
  {
    headline: 'С чего начать пить хороший кофе',
    text: 'Понятные гайды без занудства. Какие зёрна, как заваривать дома, где найти честные кофейни в Москве и Питере.',
    image_url: null,
    segment: 'Сегмент · новички',
  },
  {
    headline: 'Канал, который читают бариста',
    text: 'Разборы новых лотов, обзоры техники, конкурсы и закрытые тестинги. Для тех, кто работает за стойкой.',
    image_url: null,
    segment: 'Сегмент · профи',
  },
]

export interface StartGenerateOptions {
  channelUUID: string
  style?: CreativeStyle
  fallbackText?: string
}

export interface MockGenerationHandle {
  jobId: string
  unsubscribe: () => void
}

export function startGenerationMock(
  opts: StartGenerateOptions,
  onEvent: (event: GenerationStatusEvent) => void,
): MockGenerationHandle {
  const jobId = `mock-${Math.random().toString(36).slice(2, 10)}`
  let cancelled = false
  const stages: Array<{ status: GenerationStatus; progress: number; stage: string; delay: number }> = [
    { status: 'pending', progress: 5, stage: 'В очереди…', delay: 400 },
    { status: 'analyzing_style', progress: 18, stage: 'Анализ стиля канала…', delay: 700 },
    { status: 'analyzing_audience', progress: 32, stage: 'Анализ аудитории…', delay: 700 },
    { status: 'analyzing_problems', progress: 46, stage: 'Выявление проблем аудитории…', delay: 700 },
    { status: 'searching_references', progress: 58, stage: 'Поиск референсов…', delay: 600 },
    { status: 'generating_headlines', progress: 70, stage: 'Генерация заголовков…', delay: 700 },
    { status: 'generating_texts', progress: 82, stage: 'Генерация текстов…', delay: 700 },
    { status: 'generating_images', progress: 92, stage: 'Подбор картинок…', delay: 700 },
    { status: 'validating', progress: 96, stage: 'Финальная проверка…', delay: 400 },
  ]

  let elapsed = 0
  const timeouts: number[] = []
  for (const s of stages) {
    elapsed += s.delay
    timeouts.push(
      window.setTimeout(() => {
        if (cancelled) return
        onEvent({ job_id: jobId, status: s.status, progress: s.progress, stage: s.stage })
      }, elapsed),
    )
  }
  timeouts.push(
    window.setTimeout(() => {
      if (cancelled) return
      onEvent({
        job_id: jobId,
        status: 'completed',
        progress: 100,
        stage: 'Готово!',
        result: {
          analysis: MOCK_ANALYSIS,
          creatives: MOCK_CREATIVES_TEMPLATE.map((c, i) => ({ ...c, id: `mock-creative-${i}` })),
          analyzed_posts_count: 247,
          source: 'posts',
          platform: 'max',
          external_channel_id: '-70292750901249',
        },
      })
    }, elapsed + 600),
  )

  return {
    jobId,
    unsubscribe: () => {
      cancelled = true
      timeouts.forEach((t) => window.clearTimeout(t))
    },
  }
}

export async function startGeneration(opts: StartGenerateOptions): Promise<{ jobId: string }> {
  if (USE_MOCKS) {
    return { jobId: `mock-${Math.random().toString(36).slice(2, 10)}` }
  }
  const body: Record<string, string> = { channelUUID: opts.channelUUID }
  if (opts.style) body.style = opts.style
  if (opts.fallbackText) body.text_content = opts.fallbackText
  const res = await fetch('/api/max/channel-creatives/generate', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    console.error('[creatives] /generate failed', res.status, text)
    throw new Error(`HTTP ${res.status}`)
  }
  const data = await res.json()
  console.log('[creatives] /generate response', data)
  const jobId = data.job_id ?? data.jobId
  if (!jobId) {
    console.error('[creatives] no job_id in response', data)
    throw new Error('Сервер не вернул job_id')
  }
  return { jobId: String(jobId) }
}

export function subscribeGenerationStream(
  jobId: string,
  onEvent: (event: GenerationStatusEvent) => void,
): () => void {
  if (USE_MOCKS) {
    return () => {}
  }
  const url = `/api/max/channel-creatives/status/${encodeURIComponent(jobId)}/stream`
  console.log('[creatives] subscribing to SSE', url)
  const es = new EventSource(url, { withCredentials: true })
  es.onopen = () => console.log('[creatives] SSE open')
  let lastLoggedStatus: string | null = null
  let lastLoggedProgress = -1
  let receivedTerminal = false
  es.onmessage = (e) => {
    try {
      const event = JSON.parse(e.data) as GenerationStatusEvent
      // Логируем только смены статуса/прогресса, чтобы не засорять консоль keep-alive'ами
      if (event.status !== lastLoggedStatus || event.progress !== lastLoggedProgress) {
        console.log('[creatives] SSE event', event.status, event.progress + '%', event.stage || '')
        lastLoggedStatus = event.status
        lastLoggedProgress = event.progress
      }
      if (event.status === 'completed' || event.status === 'failed') {
        receivedTerminal = true
      }
      onEvent(event)
    } catch (err) {
      console.error('[creatives] failed to parse event', err)
    }
  }
  es.onerror = () => {
    // Если мы уже получили terminal-event (completed/failed) — это нормальное закрытие стрима бэком.
    // Иначе — реальная сетевая проблема.
    if (receivedTerminal) {
      console.log('[creatives] SSE closed normally after terminal event')
    } else {
      console.warn('[creatives] SSE closed without terminal event (network or backend)')
    }
    es.close()
  }
  return () => es.close()
}

export async function createCampaigns(
  channelUUID: string,
  creatives: Array<Pick<Creative, 'headline' | 'text' | 'image_url'>>,
): Promise<{ campaigns: Campaign[] }> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 1500))
    return {
      campaigns: [
        {
          id: 'mock-camp-1',
          name: 'Кофеманы · домашние бариста',
          segment: 'домашние бариста',
          cpc: 12,
          adsCount: 3,
          network: 'РСЯ + Поиск',
          status: 'черновик',
        },
        {
          id: 'mock-camp-2',
          name: 'Новички · «как начать»',
          segment: 'новички',
          cpc: 9,
          adsCount: 3,
          network: 'РСЯ',
          status: 'черновик',
        },
        {
          id: 'mock-camp-3',
          name: 'Профи · бариста и роастеры',
          segment: 'профи',
          cpc: 16,
          adsCount: 3,
          network: 'Поиск',
          status: 'черновик',
        },
      ],
    }
  }
  // Бэк валидирует с forbidNonWhitelisted — любое лишнее поле (id, segment,
  // client-generated поля) ловит 400 «property X should not exist». Caller часто
  // передаёт полные Creative-объекты, поэтому жёстко оставляем только разрешённые
  // поля. Без этого падало create-campaigns на креативах с segment (см. инцидент
  // mass-2026-05-25).
  const cleanCreatives = creatives.map((c) => ({
    headline: c.headline,
    text: c.text,
    image_url: c.image_url,
  }))
  console.log('[creatives] POST /create-campaigns', { channelUUID, count: cleanCreatives.length })
  const res = await fetch(
    `/api/max/channel-creatives/${encodeURIComponent(channelUUID)}/create-campaigns`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ creatives: cleanCreatives, weekly_budget: 300 }),
    },
  )
  if (!res.ok) {
    let body: { error?: string; errors?: Array<{ message?: string }>; error_details?: unknown } = {}
    try {
      body = await res.json()
    } catch {}
    console.error('[creatives] create-campaigns failed', res.status, body)
    if (res.status === 413) {
      throw new Error(
        'Слишком большой запрос — суммарный размер креативов превышает лимит. Попробуйте удалить часть креативов или уменьшить картинки.',
      )
    }
    const message =
      body.error ||
      body.errors?.[0]?.message ||
      `HTTP ${res.status}`
    const err = new Error(message) as Error & { code?: string }
    if (typeof body.error === 'string') err.code = body.error
    throw err
  }
  const data = await res.json()
  console.log('[creatives] /create-campaigns response', data)
  if (data.success === false) {
    throw new Error(data.error || 'Бэк вернул success: false без деталей')
  }
  const campaigns = data.campaigns || data.result?.campaigns || []
  return { campaigns }
}
